/**
 * Smart Lazy-Loading Form Lookup Data Hook
 *
 * Optimization Strategy:
 * - Edit Mode: Load ONLY the selected form (minimal data transport)
 * - Lazy Load: Fetch all forms only when dropdown opens
 * - Caching: Cache results to avoid re-fetching
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { FormDefinitionApiClient } from '@atlas-forms/client-js';

/**
 * Form Lookup Item
 */
export interface LookupItem {
  id: string | number;
  name: string;
  formCode: string;
  [key: string]: any; // Allow additional fields
}

/**
 * Hook State
 */
export interface UseLazyFormLookupDataState {
  data: LookupItem[];
  selectedItem: LookupItem | null;
  loading: boolean;
  error: string | null;
  hasLoadedAll: boolean;
}

/**
 * Cache storage (in-memory)
 */
const formLookupCache = new Map<string, {
  data: LookupItem[];
  timestamp: number;
  expiresIn: number; // ms
}>();

/**
 * Transform form API response to lookup items
 * Handles both a raw array and the paginated { Data: [], PageInfo: {} } envelope.
 */
const transformFormResponse = (response: any): LookupItem[] => {
  const items: any[] = Array.isArray(response)
    ? response
    : (response?.Data ?? response?.data ?? []);
  return items.map(item => ({
    ...item,
    id: item.FormID ?? item.formID ?? item.ID ?? item.id ?? '',
    name: item.Name || item.name || item.FormName || item.formName || 'Unnamed',
    formCode: item.Title || item.title || item.FormCode || item.formCode || '',
  }));
};

/**
 * Check if cache is still valid
 */
const isCacheValid = (cacheEntry: any, expiresInMs: number = 5 * 60 * 1000): boolean => {
  return Date.now() - cacheEntry.timestamp < expiresInMs;
};

/**
 * useLazyFormLookupData Hook
 *
 * Provides intelligent form lookup with lazy loading and caching
 *
 * @param selectedId - Currently selected Form ID (for edit mode)
 * @param options - Additional options
 *
 * @returns Hook state and methods
 */
export const useLazyFormLookupData = (
  selectedId?: string | number | null,
  options: {
    initialLoad?: boolean; // If true, load selected form on mount
    cacheExpiresIn?: number; // Cache expiration in ms (default: 5 minutes)
    onError?: (error: string) => void;
  } = {}
) => {
  const { initialLoad = true, cacheExpiresIn = 5 * 60 * 1000, onError } = options;

  const [state, setState] = useState<UseLazyFormLookupDataState>({
    data: [],
    selectedItem: null,
    loading: false,
    error: null,
    hasLoadedAll: false,
  });

  const cacheKey = 'lookup_forms';
  const abortControllerRef = useRef<AbortController | null>(null);
  const clientRef = useRef<FormDefinitionApiClient | null>(null);

  /**
   * Initialize API client
   */
  const getClient = useCallback(() => {
    if (!clientRef.current) {
      clientRef.current = new FormDefinitionApiClient();
    }
    return clientRef.current;
  }, []);

  /**
   * Load only the selected form (Edit Mode - Minimal Data Transport)
   */
  const loadSelectedItem = useCallback(
    async (id: string | number) => {
      if (!id) return;

      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const client = getClient();
        const response = await client.getFormById(Number(id));

        if (!response.success || !response.data) {
          throw new Error('Form not found');
        }

        const form = response.data as any;
        const item: LookupItem = {
          ...form,
          id: form.FormID ?? form.formID ?? form.ID ?? form.id ?? '',
          name: form.Name || form.name || form.FormName || form.formName || 'Unnamed',
          formCode: form.Title || form.title || form.FormCode || form.formCode || '',
        };

        setState(prev => ({
          ...prev,
          selectedItem: item,
          data: [item], // Show only selected item
          hasLoadedAll: false, // Reset to allow loading all options when dropdown opens
          loading: false,
        }));
      } catch (error: any) {
        const errorMsg = error.message || 'Failed to load form';
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMsg,
        }));
        onError?.(errorMsg);
      }
    },
    [getClient, onError]
  );

  /**
   * Load all forms (for dropdown, when user needs options)
   * Uses caching to avoid re-fetching
   */
  const loadAllRecords = useCallback(
    async (forceRefresh: boolean = false) => {
      const cacheEntry = formLookupCache.get(cacheKey);

      // Return cached data if available and not expired
      if (!forceRefresh && cacheEntry && isCacheValid(cacheEntry, cacheExpiresIn)) {
        setState(prev => ({
          ...prev,
          data: cacheEntry.data,
          hasLoadedAll: true,
        }));
        return cacheEntry.data;
      }

      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        abortControllerRef.current = new AbortController();
        const client = getClient();

        const PAGE_SIZE = 200;
        const allItems: any[] = [];

        // Fetch first page to discover total pages
        const first = await client.listForms({ PageInfo: { PageNumber: 1, PageSize: PAGE_SIZE } });
        if (!first.success || !first.data) throw new Error('Failed to load forms');

        const envelope = first.data as any;
        allItems.push(...(envelope?.Data ?? envelope?.data ?? []));

        const totalPages: number = envelope?.PageInfo?.TotalPages ?? 1;
        for (let page = 2; page <= totalPages; page++) {
          const r = await client.listForms({ PageInfo: { PageNumber: page, PageSize: PAGE_SIZE } });
          if (r.success && r.data) {
            const e = r.data as any;
            allItems.push(...(e?.Data ?? e?.data ?? []));
          }
        }

        const transformedData = transformFormResponse(allItems);

        // Cache the results
        formLookupCache.set(cacheKey, {
          data: transformedData,
          timestamp: Date.now(),
          expiresIn: cacheExpiresIn,
        });

        setState(prev => ({
          ...prev,
          data: transformedData,
          hasLoadedAll: true,
          loading: false,
        }));

        return transformedData;
      } catch (error: any) {
        // Don't fail if abort was triggered
        if (error.name === 'AbortError') {
          return;
        }

        const errorMsg = error.message || 'Failed to load forms';
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMsg,
        }));
        onError?.(errorMsg);
        return undefined;
      }
    },
    [cacheKey, cacheExpiresIn, getClient, onError]
  );

  /**
   * Lazy load on dropdown open
   * Only fetches if not already loaded
   */
  const lazyLoadOnDropdownOpen = useCallback(async () => {
    if (!state.hasLoadedAll) {
      await loadAllRecords();
    }
  }, [state.hasLoadedAll, loadAllRecords]);

  /**
   * Sync with selectedId prop:
   * - If selectedId has a value → load that form
   * - If selectedId is null/undefined → clear selectedItem so display resets
   */
  useEffect(() => {
    if (selectedId) {
      if (initialLoad) {
        loadSelectedItem(selectedId);
      }
    } else {
      // Value was cleared by parent — reset selectedItem
      setState(prev => prev.selectedItem ? { ...prev, selectedItem: null } : prev);
    }
  }, [initialLoad, selectedId, loadSelectedItem]);

  /**
   * Clear cache (useful when forms change)
   */
  const clearCache = useCallback(() => {
    formLookupCache.delete(cacheKey);
    setState(prev => ({
      ...prev,
      data: [],
      hasLoadedAll: false,
    }));
  }, [cacheKey]);

  return {
    // State
    data: state.data,
    selectedItem: state.selectedItem,
    loading: state.loading,
    error: state.error,
    hasLoadedAll: state.hasLoadedAll,

    // Methods
    loadSelectedItem,
    loadAllRecords,
    lazyLoadOnDropdownOpen,
    clearCache,
  };
};

export default useLazyFormLookupData;

/**
 * Clear the global form lookup cache.
 * Call on user logout to prevent data leakage between sessions.
 */
export const clearFormLookupCache = (): void => {
  formLookupCache.clear();
};
